import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch, watchEffect } from "vue";
  import store from "@/store";
  import { debounce } from "@/helpers/debounce";
  import { POSITION, useToast } from "vue-toastification";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import { EstateInterface } from "@/types/EstateInterface";
  import ProjectDropdown from "@/components/concierge/ProjectDropdown.vue";
  import AdminEstateStages from "@/views/admin/estates/AdminEstateStages.vue";
  import AdminStageLots from "../stages/AdminStageLots.vue";
  import { BuildersForLot } from "@/types/BuilderForLotInterface";
  import { BuilderDropdownOptionsEnum } from "@/enums/BuilderDropdownOptionsEnum";
  import BuilderDropdown from "@/components/concierge/BuilderDropdown.vue";
  import HouseTable from "@/components/concierge/HouseTable.vue";
  import FacadeOptions from "@/components/concierge/FacadeOptions.vue";
  import AddSiteCosts from "@/components/concierge/AddSiteCosts.vue";
  import SummaryPackage from "@/components/concierge/SummaryPackage.vue";
  import SavedChanges from "@/components/concierge/SavedChanges.vue";
  import { useRoute } from "vue-router";
  import router from "@/router";
  import {
    BuilderPackage,
    FacadePackage,
    FloorplanPackage,
    HousePackage,
    LotPackage,
  } from "@/types/BuilderQuote";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminPackageUpdate',
  setup(__props, { expose: __expose }) {
  __expose();

  const loading = ref(false);
  const toast = useToast();

  const packageId = router.currentRoute.value.params.packageId;

  const lotPackage = ref<LotPackage>({
    lotId: "",
    name: "",
    price: 0,
  });
  const builderPackage = ref<BuilderPackage>({
    builderId: "",
    name: "",
    logoUrl: "",
  });
  const housePackage = ref<HousePackage>({
    houseId: "",
    name: "",
    price: 0,
  });
  const facadePackage = ref<FacadePackage>({
    facadeId: "",
    name: "",
    url: "",
    price: 0,
  });
  const floorplanPackage = ref<FloorplanPackage>({
    name: "",
    url: "",
  });
  const estates = ref<EstateInterface[]>([]);
  const estateSelected = ref<string>(BuilderDropdownOptionsEnum.SELECT_PROJECT);
  const lotSelectedSize = ref<number | null>(null);
  const selectedHouseArea = ref<number | null>(null);

  const selectedEstateId = ref<string | null>(null);
  const selectedLandownerId = ref<string | null>(null);
  const selectedStageId = ref<string | null>(null);
  const selectedSiteCosts = ref<number | null>(null);

  const openListLots = ref(true);
  const openBuilderDrop = ref(true);
  const openHouseTable = ref(true);
  const openFacadeOptions = ref(true);
  const openAddSiteCosts = ref(true);

  const buildersForLot = ref<BuildersForLot>({
    builders: [],
  });

  onMounted(() => {
    getPackage();
    getEstates();
  });

  watch(
    () => lotPackage.value.lotId,
    () => {
      debounceSearch();
    }
  );

  const getPackage = async () => {
    try {
      const res = await store.dispatch("builder/getQuote", packageId);
      const packageQuote = res.data;

      selectedEstateId.value = packageQuote.estateId;
      selectedLandownerId.value = packageQuote.landownerId;

      const {
        lotId,
        name: lotName,
        price: lotPrice,
      } = packageQuote.selections.lot;

      Object.assign(lotPackage.value, {
        lotId,
        name: lotName,
        price: lotPrice,
      });

      const {
        builderId,
        name: builderName,
        logoUrl,
      } = packageQuote.selections.builder;

      Object.assign(builderPackage.value, {
        builderId,
        name: builderName,
        logoUrl,
      });

      const {
        houseId,
        name: houseName,
        price: housePrice,
      } = packageQuote.selections.house;

      Object.assign(housePackage.value, {
        houseId,
        name: houseName,
        price: housePrice,
      });

      const {
        facadeId,
        name: facadeName,
        url: facadeUrl,
        price: facadePrice,
      } = packageQuote.selections.facade;

      Object.assign(facadePackage.value, {
        facadeId,
        name: facadeName,
        url: facadeUrl,
        price: facadePrice,
      });

      const { name: floorplanName, url: floorplanUrl } =
        packageQuote.selections.floorplan;

      Object.assign(floorplanPackage.value, {
        name: floorplanName,
        url: floorplanUrl,
      });
    } catch (error) {
      toast.error("Error loading package information");
    }
  };

  const getEstates = async () => {
    try {
      const res = await store.dispatch("estate/getEstates");

      estates.value = res.data;
    } catch (error) {
      toast.error("Error getting estates");
    } finally {
      loading.value = false;
    }
  };

  const getEstate = async () => {
    try {
      const res = await store.dispatch(
        "estate/getEstate",
        selectedEstateId.value
      );

      estateSelected.value = res.data.title;
    } catch (error) {
      toast.error("Error getting estate");
    } finally {
      loading.value = false;
    }
  };

  watchEffect(() => {
    if (selectedEstateId.value) {
      getEstate();
    }
  });

  const getBuildersByLotId = async () => {
    try {
      const res = await store.dispatch(
        "builder/getBuildersByLotId",
        lotPackage.value.lotId
      );

      buildersForLot.value = res.data;
    } catch (error) {
      toast.error("Error getting builders");
    } finally {
      loading.value = false;
    }
  };

  const getHouse = async () => {
    try {
      const res = await store.dispatch(
        "builder/getHouseById",
        housePackage.value.houseId
      );

      if (res.data && res.data.length > 0) {
        selectedHouseArea.value = res.data.area;
      }
    } catch (error) {
      toast.error("An error occurred while getting the house");
    }
  };

  watchEffect(() => {
    if (housePackage.value.houseId) {
      getHouse();
    }
  });

  const getLot = async () => {
    try {
      const res = await store.dispatch(
        "lot/getOpenLot",
        lotPackage.value.lotId
      );

      if (res.data) {
        lotSelectedSize.value = res.data.size;
      }
    } catch (error) {
      toast.error("An error occurred while getting the lot");
    }
  };

  watchEffect(() => {
    if (lotPackage.value.lotId) {
      getLot();
    }
  });

  const debounceSearch = debounce(getBuildersByLotId);

  const optionsEstate = computed(() => [
    {
      title: BuilderDropdownOptionsEnum.SELECT_PROJECT,
      id: null,
      landownerId: null,
    },
    ...estates.value.map((estate) => ({
      title: estate.title,
      id: estate._id,
      landownerId: estate.landownerId._id,
    })),
  ]);

  const builderOptions = computed(() => {
    return [
      {
        title: BuilderDropdownOptionsEnum.SELECT_BUILDER,
        id: null,
        logo: null,
      },
      ...buildersForLot.value.builders.map((builder) => ({
        title: builder.name,
        id: builder._id,
        logo: builder.logo,
      })),
    ];
  });

  const selectedBuilderHouses = computed(() => {
    if (!builderPackage.value.builderId) {
      return;
    }

    const selectedBuilder = buildersForLot.value.builders.find(
      (builder) => builder._id === builderPackage.value.builderId
    );

    return selectedBuilder ? selectedBuilder.houses : [];
  });

  const onSelectEstate = (
    estateTitle: string,
    estateId: string | null,
    landownerId: string | null
  ) => {
    estateSelected.value = estateTitle;
    selectedEstateId.value = estateId;
    selectedLandownerId.value = landownerId;
    openListLots.value = false;
    openBuilderDrop.value = false;
    openHouseTable.value = false;
    openFacadeOptions.value = false;
  };

  const onSelectBuilder = (
    builderTitle: string,
    builderId: string,
    builderLogoUrl: string
  ) => {
    builderPackage.value.name = builderTitle;
    builderPackage.value.builderId = builderId;
    builderPackage.value.logoUrl = builderLogoUrl;
    openHouseTable.value = true;
    openFacadeOptions.value = false;
    openAddSiteCosts.value = false;
  };

  const onToLots = (stageId: string) => {
    openListLots.value = true;
    selectedStageId.value = stageId;
  };

  const onLotSelected = (
    lotId: string,
    lotTitle: string,
    lotSize: number,
    lotPrice: number
  ) => {
    lotPackage.value.lotId = lotId;
    lotPackage.value.name = lotTitle;
    lotSelectedSize.value = lotSize;
    lotPackage.value.price = lotPrice;
    openBuilderDrop.value = true;
    openHouseTable.value = false;
    openFacadeOptions.value = false;
    openAddSiteCosts.value = false;
    builderPackage.value.name = BuilderDropdownOptionsEnum.SELECT_BUILDER;
  };

  const onSelectHouse = (
    houseId: string,
    area: number,
    basePrice: number,
    floorplanUrl: string,
    floorplanName: string,
    houseName: string
  ) => {
    housePackage.value.houseId = houseId;
    selectedHouseArea.value = area;
    housePackage.value.price = basePrice;
    floorplanPackage.value.url = floorplanUrl;
    floorplanPackage.value.name = floorplanName;
    housePackage.value.name = houseName;
    openFacadeOptions.value = true;
  };

  const selectedHouseFacades = computed(() => {
    if (housePackage.value.houseId) {
      const selectedBuilder = buildersForLot.value.builders.find(
        (builder) => builder._id === builderPackage.value.builderId
      );

      if (selectedBuilder) {
        const selectedHouse = selectedBuilder.houses.find(
          (house) => house._id === housePackage.value.houseId
        );

        return selectedHouse ? selectedHouse.facades : [];
      }
    }

    return [];
  });

  const onSelectFacade = (
    facadeId: string,
    url: string,
    facadeName: string,
    facadePrice: number
  ) => {
    facadePackage.value.facadeId = facadeId;
    facadePackage.value.url = url;
    facadePackage.value.name = facadeName;
    facadePackage.value.price = facadePrice;
    openAddSiteCosts.value = true;
  };

  const selectedHouseSiteCosts = computed(() => {
    if (
      housePackage.value.houseId &&
      buildersForLot.value.builders.length > 0
    ) {
      const selectedBuilder = buildersForLot.value.builders.find(
        (builder) => builder._id === builderPackage.value.builderId
      );

      if (selectedBuilder) {
        const houseIndex = selectedBuilder.houses.findIndex(
          (house) => house._id === housePackage.value.houseId
        );

        if (houseIndex !== -1) {
          const siteCost = selectedBuilder.siteCosts[houseIndex];

          return siteCost !== null ? siteCost : 0;
        }
      }
    }

    return 0;
  });

  const onAddSiteCosts = (siteCosts: number) => {
    selectedSiteCosts.value = siteCosts;
  };

const __returned__ = { loading, toast, packageId, lotPackage, builderPackage, housePackage, facadePackage, floorplanPackage, estates, estateSelected, lotSelectedSize, selectedHouseArea, selectedEstateId, selectedLandownerId, selectedStageId, selectedSiteCosts, openListLots, openBuilderDrop, openHouseTable, openFacadeOptions, openAddSiteCosts, buildersForLot, getPackage, getEstates, getEstate, getBuildersByLotId, getHouse, getLot, debounceSearch, optionsEstate, builderOptions, selectedBuilderHouses, onSelectEstate, onSelectBuilder, onToLots, onLotSelected, onSelectHouse, selectedHouseFacades, onSelectFacade, selectedHouseSiteCosts, onAddSiteCosts, computed, onMounted, ref, watch, watchEffect, get store() { return store }, get debounce() { return debounce }, get POSITION() { return POSITION }, get useToast() { return useToast }, DropDown, get EstateInterface() { return EstateInterface }, ProjectDropdown, AdminEstateStages, AdminStageLots, get BuildersForLot() { return BuildersForLot }, get BuilderDropdownOptionsEnum() { return BuilderDropdownOptionsEnum }, BuilderDropdown, HouseTable, FacadeOptions, AddSiteCosts, SummaryPackage, SavedChanges, get useRoute() { return useRoute }, get router() { return router }, get BuilderPackage() { return BuilderPackage }, get FacadePackage() { return FacadePackage }, get FloorplanPackage() { return FloorplanPackage }, get HousePackage() { return HousePackage }, get LotPackage() { return LotPackage } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})